import type { TFunction } from 'i18next';

/**
 * Turning a purchase refusal into something the buyer can read.
 *
 * ⚠️ THE SERVER'S `message` IS ENGLISH, ALWAYS. It is written for a log and for
 * a client that has no translation of its own — never for the person on a
 * Japanese booking page. Every surface here used to render it directly
 * (`err.response.data.message || t(...)`), which meant the fallback translation
 * could never win: a Japanese buyer stopped at checkout was told, in English,
 * that their email already had an account.
 *
 * So the CODE is what we read. `error` is a stable slug the server already
 * sends on every refusal, and this is the one place that maps slugs to copy.
 * The server's `message` survives only as the last resort for a code nobody has
 * translated yet — better an English sentence than a shrug, but it should never
 * be the normal path. Add a case here when you add one to `PURCHASE_ERRORS`.
 *
 * MOST OF THESE ARE THE HOST'S PROBLEM, NOT THE BUYER'S. A pack with no price,
 * a plan with no interval, a product that is not really a pack — the person
 * holding the card can do nothing about any of it, and does not benefit from
 * being told which field is missing. They are deliberately collapsed into one
 * "not set up yet, contact the host" line rather than translated into a dozen
 * near-identical sentences that all mean the same thing.
 */
export const purchaseErrorMessage = (
  err: any,
  t: TFunction,
  /**
   * What to say when the code is one we do not know. Each surface has its own
   * — "we could not change your plan" is the wrong thing to read on a screen
   * that was buying a point pack — so it is passed in rather than assumed.
   */
  fallback?: string
): string => {
  const data = err?.response?.data;
  const code = typeof data?.error === 'string' ? data.error : '';

  switch (code) {
    /**
     * The one refusal that is genuinely the buyer's to act on: their address
     * has an account, so they sign in. Everything else here is information.
     */
    case 'PASSWORD_REQUIRED':
      return t(
        'booking:purchase.passwordRequired',
        'This email already has an account. Please enter your password to continue.'
      );

    // Nothing to sell, for whatever reason. A withdrawn product, a deactivated
    // one, and one the host's account has not been released all look identical
    // from outside — and should, or the page starts advertising what it hides.
    case 'PRODUCT_NOT_FOUND':
    case 'PRODUCT_INACTIVE':
    case 'FEATURE_NOT_RELEASED':
      return t('booking:purchase.errors.unavailable', 'This product is not available.');

    // Misconfigured by the host. See the note above on why these are one line.
    case 'NOT_A_POINT_PACK':
    case 'NOT_A_SUBSCRIPTION':
    case 'PACK_GRANTS_NO_POINTS':
    case 'PACK_HAS_NO_PRICE':
    case 'PLAN_HAS_NO_INTERVAL':
    case 'PLAN_HAS_NO_PRICE':
    case 'PLAN_HAS_NO_ALLOWANCE':
    case 'PROVIDER_NO_RECURRING_SUPPORT':
      return t(
        'booking:purchase.errors.notSetUp',
        'This product is not ready to be sold yet. Please contact the host.'
      );

    // Named separately because it says something true and specific — the price
    // itself is the problem, not the checkout.
    case 'AMOUNT_BELOW_MINIMUM':
      return t(
        'booking:purchase.errors.belowMinimum',
        'This is priced below the minimum a card payment can take. Please contact the host.'
      );

    case 'PAYMENT_ACCOUNT_NOT_CONNECTED':
    case 'PAYMENT_ACCOUNT_NOT_READY':
      return t('booking:purchase.errors.hostCannotTakePayments', 'This host cannot take payments yet.');

    case 'email_required':
      return t('booking:purchase.emailRequired', 'Enter the email your points should go to.');

    /**
     * The session went away underneath them — expired, or cleared in another
     * tab. Say so, because the sign-in that fixes it is not obvious from a
     * screen that still looks signed in.
     */
    case 'unauthenticated':
      return t(
        'booking:purchase.errors.sessionExpired',
        'Your session has ended. Please sign in again.'
      );

    default:
      return (
        data?.message ||
        fallback ||
        t('booking:purchase.startFailed', 'Could not start the payment. Please try again.')
      );
  }
};

export default purchaseErrorMessage;
